import type { FlowOpportunity } from '../services/smartFlowService';

export type FlowAlertOperator =
  | '>'
  | '<'
  | '>='
  | '<='
  | '='
  | '!='
  | 'contains'
  | 'not_contains';

export type AlertDataSource =
  | 'flow_opportunity'
  | 'questdb_flow'
  | 'stable_snapshot';

export interface CustomAlertFieldDef {
  id: string;
  label: string;
  type: 'number' | 'string' | 'boolean';
  expression?: string;
  description?: string;
  source?: AlertDataSource;
}

export interface FlowAlertRule {
  id: string;
  field: string;
  operator: FlowAlertOperator;
  value: string | number;
  enabled: boolean;
  source?: AlertDataSource;
  questdb_filters?: Record<string, unknown>;
}

type AlertRow = FlowOpportunity | Record<string, unknown>;

const FIELD_ALIASES: Record<string, string> = {
  prem: 'premium_size',
  liquidity: 'premium_size',
  oi: 'open_interest',
  Open: 'open_interest',
  'Open Interest': 'open_interest',
  OI: 'oi',
  Premium: 'premium',
  Size: 'size',
  Vol: 'volume',
  Ticker: 'ticker',
  Underlying: 'symbol',
};

function resolveFieldKey(fieldKey: string): string {
  return FIELD_ALIASES[fieldKey] || fieldKey;
}

/** Safe numeric expression: identifiers + max(a,b) + basic ops */
export function evaluateCustomExpression(
  expression: string,
  item: AlertRow,
  customFields?: CustomAlertFieldDef[],
): number | null {
  const expr = (expression || '').trim();
  if (!expr) return null;
  const allowed = /^[a-zA-Z0-9_./\s+\-*/(),]+$/;
  if (!allowed.test(expr)) return null;

  const resolveId = (id: string): number => {
    const cf = customFields?.find((c) => c.id === id);
    if (cf?.expression && cf.id !== id) {
      const nested = evaluateCustomExpression(cf.expression, item, customFields);
      return nested ?? 0;
    }
    const raw = getFlowAlertFieldValue(item, id, customFields);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  let substituted = expr.replace(/\bmax\s*\(/gi, 'Math.max(');
  const ids = substituted.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
  const reserved = new Set(['Math', 'max', 'min']);
  for (const id of ids) {
    if (reserved.has(id)) continue;
    const val = resolveId(resolveFieldKey(id));
    substituted = substituted.replace(new RegExp(`\\b${id}\\b`, 'g'), String(val));
  }
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(`"use strict"; return (${substituted});`);
    const result = fn();
    return typeof result === 'number' && Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
}

export function getFlowAlertFieldValue(
  item: AlertRow,
  fieldKey: string,
  customFields?: CustomAlertFieldDef[],
): unknown {
  const key = resolveFieldKey(fieldKey);
  const custom = customFields?.find((c) => c.id === key || c.id === fieldKey);
  if (custom?.expression) {
    return evaluateCustomExpression(custom.expression, item, customFields);
  }

  const row = item as Record<string, unknown>;
  const fo = item as FlowOpportunity;
  switch (key) {
    case 'strike_price':
    case 'strike':
      return fo.strike_price ?? row.strike ?? row.Strike;
    case 'volume':
    case 'Volume':
      return fo.volume ?? row.volume ?? row.Volume;
    case 'open_interest':
      return fo.open_interest ?? row.open_interest ?? row.oi ?? row.OI ?? row['Open Interest'];
    case 'oi':
      return row.oi ?? row.OI ?? fo.open_interest;
    case 'size':
      return row.size ?? row.Size ?? fo.total_trade_size;
    case 'dte':
      return row.dte ?? row.DTE;
    case 'stock':
    case 'spot':
      return row.stock ?? row.spot ?? row.Spot ?? fo.underlying_price;
    case 'bid':
      return row.bid ?? row.Bid;
    case 'ask':
      return row.ask ?? row.Ask;
    case 'iv':
      return row.iv ?? row['Implied Volatility'];
    case 'delta':
      return row.delta ?? row.Delta;
    case 'gamma':
      return row.gamma ?? row.Gamma;
    case 'theta':
      return row.theta ?? row.Theta ?? fo.theta;
    case 'vega':
      return row.vega ?? row.Vega;
    case 'ticker':
      return row.ticker ?? row.Ticker ?? fo.symbol;
    case 'contract':
      return row.contract ?? row.Contract ?? fo.contract_ticker;
    case 'last_price':
      return fo.last_price ?? row.Last;
    case 'flow_score':
      return fo.flow_score;
    case 'premium_size':
    case 'premium':
      return fo.premium_size ?? fo.premium ?? row.premium ?? row.Premium ?? 0;
    case 'unusual_activity_score':
      return fo.unusual_activity_score;
    case 'trade_count':
      return fo.trade_count;
    case 'total_trade_size':
      return fo.total_trade_size;
    case 'avg_trade_price':
      return fo.avg_trade_price;
    case 'option_type':
      return fo.option_type ?? row.option_type ?? row['Option Type'];
    case 'moneyness':
      return fo.moneyness ?? row.MoneynessCategory ?? row.Moneyness;
    case 'symbol':
      return fo.symbol ?? row.ticker ?? row.Underlying;
    case 'expiry_date':
      return fo.expiry_date ?? row.expiry ?? row['Expiry Date'];
    case 'contract_ticker':
      return fo.contract_ticker ?? row.contract;
    case 'execution_side':
      return fo.execution_side ?? '';
    case 'side_bid_ask':
      return fo.side_bid_ask ?? row.side ?? row.Side ?? '';
    case 'volume_to_oi_ratio':
    case 'Vol/OI':
      {
        const oi = Number(fo.open_interest ?? row.oi ?? row['Open Interest'] ?? 0);
        const vol = Number(fo.volume ?? row.volume ?? row.Volume ?? 0);
        return oi > 0 ? vol / oi : 0;
      }
    case 'Delta Premium':
      return row['Delta Premium'];
    case 'Delta Accumulated Premium':
      return row['Delta Accumulated Premium'];
    case 'Bid Volume':
      return row['Bid Volume'];
    case 'Ask Volume':
      return row['Ask Volume'];
    case 'Sentiment':
      return row.Sentiment ?? row.sentiment;
    case 'Underlying':
      return row.Underlying ?? fo.symbol;
    case 'Symbol':
      return row.Symbol ?? fo.symbol;
  }
  if (row[key] !== undefined) return row[key];
  if (row[fieldKey] !== undefined) return row[fieldKey];
  return (fo as unknown as Record<string, unknown>)[key];
}

export function evaluateFlowAlertRule(
  item: AlertRow,
  rule: FlowAlertRule,
  customFields?: CustomAlertFieldDef[],
): boolean {
  if (!rule.enabled) return false;
  const fieldValue = getFlowAlertFieldValue(item, rule.field, customFields);
  if (fieldValue === null || fieldValue === undefined) return false;

  switch (rule.operator) {
    case '>':
      return Number(fieldValue) > Number(rule.value);
    case '<':
      return Number(fieldValue) < Number(rule.value);
    case '>=':
      return Number(fieldValue) >= Number(rule.value);
    case '<=':
      return Number(fieldValue) <= Number(rule.value);
    case '=':
      return (
        String(fieldValue).toLowerCase() === String(rule.value).toLowerCase()
      );
    case '!=':
      return (
        String(fieldValue).toLowerCase() !== String(rule.value).toLowerCase()
      );
    case 'contains':
      return String(fieldValue)
        .toLowerCase()
        .includes(String(rule.value).toLowerCase());
    case 'not_contains':
      return !String(fieldValue)
        .toLowerCase()
        .includes(String(rule.value).toLowerCase());
    default:
      return false;
  }
}

export function formatPremiumUsd(n: number): string {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
}

export function formatContractLabel(item: FlowOpportunity): string {
  const sym = item.symbol || '';
  const strike = item.strike_price != null ? String(item.strike_price) : '';
  const type =
    item.option_type === 'call'
      ? 'C'
      : item.option_type === 'put'
        ? 'P'
        : item.call_put || '';
  const exp = item.expiry_date || item.exp_display || '';
  const ticker = item.contract_ticker || '';
  if (ticker) return ticker;
  return [sym, strike, type, exp].filter(Boolean).join(' ');
}

export function formatFlowLiquidityNotification(item: FlowOpportunity): {
  title: string;
  body: string;
} {
  const premium = Number(item.premium_size ?? item.premium ?? 0);
  const vol = Number(item.volume ?? 0);
  const contract = formatContractLabel(item);
  const sideRaw = String(
    item.execution_side || item.side_bid_ask || '',
  ).toLowerCase();
  const sideLabel =
    sideRaw.includes('buy') || sideRaw.includes('ask')
      ? 'Buy'
      : sideRaw.includes('sell') || sideRaw.includes('bid')
        ? 'Sell'
        : sideRaw
          ? sideRaw
          : 'Flow';
  const liq = formatPremiumUsd(premium);
  const title = `${liq} liquidity · ${contract}`;
  const parts = [
    `${sideLabel} volume ${vol.toLocaleString()}`,
    item.symbol ? `underlying ${item.symbol}` : '',
    item.moneyness ? item.moneyness : '',
    item.flow_score ? `score ${Number(item.flow_score).toFixed(1)}` : '',
  ].filter(Boolean);
  return { title, body: parts.join(' · ') };
}

export function itemMatchesFlowAlertRules(
  item: AlertRow,
  rules: FlowAlertRule[],
  customFields?: CustomAlertFieldDef[],
): boolean {
  const enabled = rules.filter((r) => r.enabled);
  if (!enabled.length) return false;
  return enabled.every((rule) => evaluateFlowAlertRule(item, rule, customFields));
}

export function itemMatchesBigFlowFloor(
  item: FlowOpportunity,
  minPremium: number,
  minVolume: number,
): boolean {
  const premium = Number(item.premium_size ?? item.premium ?? 0);
  const vol = Number(item.volume ?? 0);
  if (minPremium > 0 && premium >= minPremium) return true;
  if (minVolume > 0 && vol >= minVolume) return true;
  return false;
}
